/* Onboarding and leaving — the queue.
 *
 * The register already fills itself: the nightly sync creates a row for every
 * new account in the tenant, so a starter is in Blueprint the morning after HR
 * finish in AD. What it cannot do is the part that needs a person — deciding
 * whether the new account is a receptionist or a scanner, what depot they work
 * at, which laptop they were handed. This screen is the list of accounts
 * waiting for somebody to do that, and the flow for doing it.
 *
 * WHY IT IS NOT THE PROCEDURES CHECKLIST
 *
 * There is already a checklist mechanism, and its own comment says it was meant
 * for exactly this. But a step there is a string and ticking one appends an
 * integer to a JSON array — it records that somebody SAID they did something,
 * not that it happened. Onboarding needs steps that write a depot, assign a
 * laptop and call a Function, each of which can fail on its own. A tick cannot
 * fail, which is why the two are different things.
 *
 * THE QUEUE IS NOISIER THAN "NEW STARTERS", ON PURPOSE
 *
 * Every new account lands here, including shared mailboxes, scanners and
 * service accounts, because nothing else ever asks anybody what they are. One
 * click takes a scanner back out, and the alternative — showing only accounts
 * that already look like people — would mean the accounts nobody has described
 * stay invisible, which is the state the register started in.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDepotScope, scoped } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { fmtDate, initials } from '../../lib/format';
import {
  isOffboarding,
  isOnboarding,
  needsWelcome,
  onboardingSteps,
  recordGaps,
  whenWelcomed,
} from '../../lib/lifecycle';
import { rowTarget } from '../../lib/joiner';
import { isUnclassified } from '../../lib/registers';
import type { IJoinerCase, IPerson, Tone } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  SearchBox,
  Select,
  SummaryTile,
  SummaryTiles,
  TableSkeleton,
  type Column,
} from '../ui';
import { Explain } from './Explain';
import { LifecycleDrawer } from './LifecycleDrawer';
import s from './screens.module.scss';

type Lens = 'starters' | 'leavers' | 'welcome' | 'incomplete' | 'completed' | 'team' | 'all';

/** The leavers' half of the same screen, under its own name so it can be routed to and
 *  tested like any other. */
export const OffboardingScreen = (): JSX.Element => <OnboardingScreen mode="offboarding" />;

export function OnboardingScreen({ mode = 'onboarding' }: { mode?: 'onboarding' | 'offboarding' }): JSX.Element {
  const offboarding = mode === 'offboarding';
  const svc = useServices();
  const navigate = useNavigate();
  const { scope } = useDepotScope();
  const depots = useDepots();
  const depotName = (id: string): string => svc.depots.name(id);

  const [lens, setLens] = useState<Lens>(offboarding ? 'leavers' : 'starters');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<IPerson | null>(null);

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });

  const all = useMemo(() => people.data ?? [], [people.data]);

  /* The Onboarding app's cases, when it is configured. Somebody IT has handed over is with HR or
     Accounts now: out of IT's queue (there is nothing left for IT to do) and out of the welcome list
     (the welcome goes when HR finishes), but still visible, under their own view. */
  const cases = useQuery({
    queryKey: qk.joinerCases,
    queryFn: () => svc.joiners!.cases(),
    enabled: !!svc.joiners,
  });
  const caseOf = useMemo(() => {
    const m = new Map<string, IJoinerCase>();
    (cases.data ?? []).forEach((c) => { if (c.stage !== 'Complete') m.set(c.upn.toLowerCase(), c); });
    return (p: IPerson): IJoinerCase | undefined => m.get((p.upn || '').toLowerCase());
  }, [cases.data]);

  const starters = useMemo(() => all.filter(isOnboarding).filter((p) => !caseOf(p)), [all, caseOf]);
  const withTeam = useMemo(() => all.filter((p) => !!caseOf(p)), [all, caseOf]);
  const leavers = useMemo(() => all.filter(isOffboarding), [all]);
  /* Everybody already set up whose record is still missing something. These are
     the gaps most worth fixing and the ones least likely to be noticed: the
     backfill stamped every row that existed before this screen did, so none of
     these people will ever appear in the starters queue. */
  const incomplete = useMemo(() => all.filter((p) => recordGaps(p).length > 0), [all]);
  /* Everybody who can sign in and has not been told so. A separate job from
     onboarding and done at a different time: access was given to everyone at
     once, the welcomes go out by hand over the following days. */
  const unwelcomed = useMemo(() => all.filter((p) => needsWelcome(p)).filter((p) => !caseOf(p)), [all, caseOf]);
  /* Everybody onboarding has been finished for, newest first by default, so "who have I done"
     can be answered without scrolling through the whole register. Leavers are not in it. */
  const completed = useMemo(() => all.filter((p) => p.onboarded && !isOffboarding(p)), [all]);

  const rows = useMemo(() => {
    const base =
      lens === 'starters' ? starters
        : lens === 'leavers' ? leavers
          : lens === 'welcome' ? unwelcomed
            : lens === 'incomplete' ? incomplete
              : lens === 'completed' ? completed
                : lens === 'team' ? withTeam
                  : all;
    const q = query.trim().toLowerCase();
    return scoped(base, scope).filter((p) =>
      !q || [p.name, p.email, p.upn, p.title, p.dept].some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [lens, starters, leavers, incomplete, completed, withTeam, all, query, scope]);

  /** The first thing still to do, which is the only cell on this screen anybody
   *  reads twice. */
  function stage(p: IPerson): { label: string; tone: Tone } {
    /* First, not last. This column answers whatever the lens is asking, and in
       the welcome lens "Complete" is true but useless — everybody in that list
       is complete, which is why they are ready to be welcomed. It was below the
       onboarded check and so never ran. */
    if (lens === 'welcome') {
      return p.welcomeSentOn
        ? { label: `Welcomed ${whenWelcomed(p.welcomeSentOn)}`, tone: 'ok' }
        : { label: 'Not welcomed', tone: 'warn' };
    }

    if (isOffboarding(p)) {
      /* First: a deleted account keeps its old status (Active), so the Leaver test below would
         have called it a leaver with a last day. It is already gone. */
      if (p.missingSince) return { label: `Deleted in 365 ${fmtDate(p.missingSince)}`, tone: 'bad' };
      return p.status === 'Leaver'
        ? { label: 'Already gone', tone: 'bad' }
        : { label: `Leaves ${fmtDate(p.leavingOn)}`, tone: 'warn' };
    }
    if (lens === 'team') {
      const c = caseOf(p);
      return { label: c?.stage === 'Accounts' ? 'With Accounts' : 'With HR', tone: 'info' };
    }
    if (lens === 'completed') {
      return { label: `Completed ${fmtDate(p.onboarded!)}`, tone: 'ok' };
    }
    const gaps = recordGaps(p);
    if (p.onboarded) {
      return gaps.length > 0
        ? { label: gaps.map((g) => g.detail).join(', '), tone: 'warn' }
        : { label: 'Complete', tone: 'ok' };
    }
    const outstanding = onboardingSteps(p, devices.data ?? [], p.id).find((x) => x.state === 'todo');
    return outstanding
      ? { label: outstanding.label, tone: isUnclassified(p) ? 'warn' : 'info' }
      : { label: 'Ready to finish', tone: 'ok' };
  }

  const columns: Column<IPerson>[] = [
    {
      key: 'name',
      header: 'Name',
      sortBy: (p) => p.name,
      render: (p) => (
        <div className={s.person}>
          <div className={`${s.chip} ${isOffboarding(p) ? '' : s.chipBlue}`}>{initials(p.name)}</div>
          <div>
            <div className={s.cellMain}>{p.name}</div>
            <div className={s.cellSub}>{p.email || p.upn}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'title',
      priority: 2,
      header: 'Role',
      sortBy: (p) => p.title,
      render: (p) => (
        <>
          <div>{p.title || <span className={s.cellSub}>no job title</span>}</div>
          <div className={s.cellSub}>{p.dept}</div>
        </>
      ),
    },
    {
      key: 'kind',
      priority: 3,
      header: 'Account',
      sortBy: (p) => p.kind ?? '',
      render: (p) =>
        isUnclassified(p)
          ? <Badge tone="warn">Unclassified</Badge>
          : <span>{p.kind}</span>,
    },
    { key: 'loc', priority: 3, header: 'Depot', sortBy: (p) => p.loc, render: (p) => depotName(p.loc) || '—' },
    {
      key: 'stage',
      header: 'Outstanding',
      sortBy: (p) => stage(p).label,
      render: (p) => {
        const st = stage(p);
        return <Badge tone={st.tone}>{st.label}</Badge>;
      },
    },
    /* Clicking a new starter opens Add a joiner. The old side panel is still one click away for the odd
       person who is part-way through the old way, and it is how everything in the other views opens. */
    ...(lens === 'starters' && svc.joiners
      ? [{
        key: 'record',
        header: '',
        render: (p: IPerson) => (rowTarget(p, lens, true) === 'wizard'
          ? (
            <button
              type="button"
              className={s.cellSub}
              style={{ background: 'none', border: 0, padding: 0, textDecoration: 'underline', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); setOpen(p); }}
            >
              Open record
            </button>
          )
          : null),
      }]
      : []),
  ];

  if (people.isError) return <LoadError what="people" error={people.error} />;

  return (
    <>
      <SummaryTiles large>
        {offboarding ? (
          <SummaryTile icon="ti-user-minus" value={leavers.length} label="Leaving" tone={leavers.length > 0 ? 'warn' : 'ok'} />
        ) : (
          <>
            {/* Each tile is the filter for the list beneath it, the same as the choice in the
                Showing box, so the number and the list it counts are one click apart. */}
            <SummaryTile icon="ti-user-plus" value={starters.length} label="To set up" tone={starters.length > 0 ? 'warn' : 'ok'}
              active={lens === 'starters'} onClick={() => setLens('starters')} />
            {svc.joiners && (
              <SummaryTile icon="ti-users" value={withTeam.length} label="With HR or Accounts"
                active={lens === 'team'} onClick={() => setLens('team')} />
            )}
            <SummaryTile icon="ti-mail" value={unwelcomed.length} label="To welcome" tone={unwelcomed.length > 0 ? 'warn' : 'ok'}
              active={lens === 'welcome'} onClick={() => setLens('welcome')} />
            <SummaryTile icon="ti-circle-check" value={completed.length} label="Completed" tone="ok"
              active={lens === 'completed'} onClick={() => setLens('completed')} />
            <SummaryTile icon="ti-alert-triangle" value={incomplete.length} label="Incomplete records"
              active={lens === 'incomplete'} onClick={() => setLens('incomplete')} />
            <SummaryTile icon="ti-users" value={all.length} label="Accounts"
              active={lens === 'all'} onClick={() => setLens('all')} />
          </>
        )}
      </SummaryTiles>

      {offboarding ? (
        <Explain title="What offboarding somebody does">
          <p>
            Open a leaver, collect their equipment, then press Offboard. It shows you what will
            happen first and changes nothing until you confirm: it blocks their sign-in and ends
            their sessions, takes away their hub access, removes them from their groups, closes
            their folder and marks them a leaver in EZ Leave, then tells you what is left.
          </p>
          <p>
            What it cannot do is named, with the reason: groups run by a rule or from on-premises,
            Entra administrator roles, mailing lists, department document libraries (Hub Admin) and
            their mailbox (Exchange).
          </p>
        </Explain>
      ) : (
      <Explain title="What this screen does, and what it still does not">
        <p>
          The nightly sync brings every new account in from Entra, so a starter appears here the
          morning after the account is made. Nothing is created here — this is the list of accounts
          waiting for somebody to say what they are and fill in what Entra cannot supply.
        </p>
        <p>
          Shared mailboxes, scanners and service accounts arrive in the queue too, because nothing
          else ever asks anybody what they are. Classifying one takes it straight back out.
        </p>
        <p>
          Leavers are on the Offboarding page.
        </p>
      </Explain>
      )}

      <Controls count={`${rows.length}${scope !== 'all' ? ` at ${depotName(scope)}` : ''}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Search name, email, role or department" />
        {offboarding ? null : <Select
          label="Showing"
          value={lens}
          onChange={(v) => setLens(v as Lens)}
          options={[
            { value: 'starters', label: `To set up (${starters.length})` },
            ...(svc.joiners ? [{ value: 'team', label: `With HR or Accounts (${withTeam.length})` }] : []),
            { value: 'welcome', label: `To welcome (${unwelcomed.length})` },
            { value: 'completed', label: `Completed (${completed.length})` },
            /* Deliberately not folded into the starters queue. These people were
               set up long ago; their records are simply incomplete, and mixing
               the two would make the queue impossible to get to the bottom of. */
            { value: 'incomplete', label: `Incomplete records (${incomplete.length})` },
            { value: 'all', label: 'Everybody' },
          ]}
        />}
        {/* The guided flow for somebody new, shown only once the Onboarding app is configured: until
            then there is nowhere to hand a joiner to. This screen stays for finishing people already
            started and for the welcome list. */}
        {offboarding || !svc.joiners ? null : (
          <Button primary icon="ti-user-plus" onClick={() => navigate('/joiner')}>Add a joiner</Button>
        )}
      </Controls>

      {people.isLoading ? (
        <TableSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={lens === 'starters' ? 'ti-circle-check' : 'ti-users'}
          title={
            lens === 'starters' ? 'Nobody waiting to be set up'
              : lens === 'leavers' ? 'Nobody leaving'
                : lens === 'welcome' ? 'Everybody has been welcomed'
                : lens === 'team' ? 'Nobody is with HR or Accounts'
                : lens === 'completed' ? 'Nobody has been onboarded yet'
                : lens === 'incomplete' ? 'Every record is complete'
                  : 'No accounts'
          }
        >
          {lens === 'starters'
            ? 'New accounts appear here the morning after they are created in Entra.'
            : null}
        </EmptyState>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(p) => p.id}
          initialSort={{ key: 'name', dir: 'asc' }}
          onRowClick={(p) => (rowTarget(p, lens, !!svc.joiners) === 'wizard' ? navigate(`/joiner/${p.id}?step=role`) : setOpen(p))}
          selectedKey={open?.id}
        />
      )}

      <LifecycleDrawer
        person={open}
        devices={devices.data ?? []}
        depots={depots}
        onClose={() => setOpen(null)}
      />
    </>
  );
}
